// useClients.ts
import { useQuery } from "@tanstack/react-query";
import { getClients } from "../api";
import type { Client } from "../../types";

interface ClientsResponse {
  clients: Client[];
  next: string | null;
  previous: string | null;
  totalClients: number;
  totalPages: string;
  availableTaxYears?: number[];
}

interface UseClientsOptions {
  limit?: number;
  page?: number;
  status?: string;
  order?: string;
  assignedTo?: any[];
  tagIds?: string[];
  sortBy?: string;
  search?: string;
  includeArchived?: boolean;
  taxYear?: number | null;
  organizerStatus?: string;
}

export const useClients = (options: UseClientsOptions = {}) => {
  const {
    limit = 5,
    page,
    status,
    order,
    assignedTo,
    tagIds,
    sortBy,
    search,
    includeArchived,
    taxYear,
    organizerStatus,
  } = options;

  const queryKey = [
    "clients",
    {
      limit,
      page,
      status,
      order,
      assignedTo,
      tagIds,
      sortBy,
      search,
      includeArchived,
      taxYear,
      organizerStatus,
    },
  ];

  const {
    data,
    isLoading: isLoadingClients,
    isError: isErrorClients,
    error: errorClients,
    refetch,
  } = useQuery<ClientsResponse, Error>({
    queryKey,
    queryFn: async () => {
      const response = await getClients({
        limit,
        page,
        status,
        order,
        assignedTo,
        tagIds,
        sortBy,
        search,
        includeArchived,
        taxYear,
        organizerStatus,
      });
      return response;
    },
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Do refetch on reconnect
  });

  return {
    clients: data?.clients,
    totalClients: data?.totalClients,
    totalPages: data?.totalPages,
    availableTaxYears: data?.availableTaxYears || [],
    isLoadingClients,
    isErrorClients,
    errorClients,
    refetch,
  };
};
